import Model, { attr, belongsTo } from '@ember-data/model';
import type { AsyncBelongsTo } from '@ember-data/model';
import adapter from 'garaje/utils/decorators/adapter';

import type EmployeeModel from './employee';
import type EntryModel from './entry';
import type InviteModel from './invite';
import type LocationModel from './location';
import type ReservationModel from './reservation';
import type ZoneModel from './zone';

@adapter('badge-log')
class AccessEventModel extends Model {
  // attributes
  @attr('string') declare eventId: string;
  @attr('string') declare integrationName: string;
  @attr('string') declare integrationType: string;
  @attr('date') declare messageTimestamp: Date;
  @attr('date') declare receivedAtTimestamp: Date;
  @attr('date') declare eventTimestamp: Date;
  @attr('string') declare personEmail: string;
  @attr('string') declare personType: string;
  @attr('string') declare personName?: string;
  @attr('number') declare deliveryAttempt: number;
  @attr('string') declare failureReason?: string;
  @attr('string') declare portalName?: string;
  @attr('string') declare portalType?: string;
  @attr('string') declare eventType?: string;
  @attr('string') declare macAddress?: string;
  @attr('string') declare ssidName?: string;
  @attr('string') declare geoCity?: string;
  @attr('string') declare geoState?: string;
  @attr('string') declare geoCountry?: string;
  @attr('string') declare ipSubnet?: string;
  @attr('boolean') declare outOfWindow: boolean | null;
  @attr('boolean') declare createdEntry?: boolean | null;

  // New attributes for person grouping
  @attr('string') declare employeeId?: string;
  @attr('string') declare activityType?: string;
  @attr('string') declare entranceName?: string;
  @attr('string') declare entryId?: string;
  // Set only on the signals that corroborated a multi-signal sign-in (the held signals + the tipping
  // sign-in). Distinct from entryId, which marks any association with the entry. See Presence Heuristics M1.
  @attr('string') declare corroboratedEntryId?: string;

  // relationships
  @belongsTo('entry', { async: true, inverse: null }) declare entry?: AsyncBelongsTo<EntryModel>;
  @belongsTo('invite', { async: true, inverse: null }) declare invite?: AsyncBelongsTo<InviteModel>;
  @belongsTo('reservation', { async: true, inverse: null }) declare reservation?: AsyncBelongsTo<ReservationModel>;
  @belongsTo('zone', { async: true, inverse: null }) declare zone?: AsyncBelongsTo<ZoneModel>;
  @belongsTo('location', { async: true, inverse: null }) declare location?: AsyncBelongsTo<LocationModel>;
  @belongsTo('employee', { async: true, inverse: null }) declare employee?: AsyncBelongsTo<EmployeeModel>;

  get displayName(): string {
    return this.personName || this.personEmail || 'Unknown';
  }

  get signInLocation(): string {
    return [this.geoCity, this.geoState, this.geoCountry].filter(Boolean).join(', ');
  }

  // Display the /16 prefix as e.g. "192.168.x.x" so the truncation is obvious to admins.
  get ipSubnetDisplay(): string {
    return this.ipSubnet ? `${this.ipSubnet}.x.x` : '';
  }

  // Computed properties for outcome
  get outcome(): string {
    if (this.createdEntry && !this.failureReason) {
      return 'Signed in';
    }
    if (this.failureReason) {
      return 'Failed';
    }
    if (!this.createdEntry && this.failureReason) {
      return 'Not processable';
    }
    return '-';
  }

  get humanReadableEventType(): string {
    const map: Record<string, string> = {
      SIGN_IN: 'Signed in',
      SIGN_OUT: 'Signed out',
    };
    const raw = this.activityType ?? this.eventType;
    if (!raw) return '';
    return (
      map[raw] ??
      raw
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/^\w/, (c) => c.toUpperCase())
    );
  }

  get outcomeBadgeColor(): string {
    switch (this.outcome) {
      case 'Signed in':
        return 'success';
      case 'Failed':
        return 'error';
      case 'Not processable':
        return 'warning';
      default:
        return 'neutral';
    }
  }

  // True for the sign-in row that a multi-signal policy produced: it created an entry and carries the
  // corroboration marker. The held contributing signals share corroboratedEntryId but did not create
  // an entry, so they are excluded here.
  get isMultiSignalSignIn(): boolean {
    return Boolean(this.corroboratedEntryId) && Boolean(this.createdEntry);
  }
}

export default AccessEventModel;

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'access-event': AccessEventModel;
  }
}
